import { Component } from "@angular/core";
import { tap, map, catchError } from "rxjs/operators";
import { State } from "@progress/kendo-data-query";
import { FormGroup, Validators, FormControl, AbstractControl, ValidationErrors } from "@angular/forms";
import { EnvironmentDataService } from "../service/environment-data.service";
import { Environment } from "../models/environment";
import { InlineEditGridComponent } from "../../core/inline-edit-grid.component";
import { DialogService, DialogRef } from "@progress/kendo-angular-dialog";
import { Observable, of } from 'rxjs';
import { EnvironmentServiceDataService } from '../service/environment-service-data.service';

@Component({
  selector: "app-environments",
  templateUrl: "./environments.component.html",
  styleUrls: ["./environments.component.scss"],
})
export class EnvironmentsComponent extends InlineEditGridComponent {
  public environmentTypeMap: Map<string, number> = new Map();

  public listItems: Array<{ text: string; value: number }> = [
    { text: "Development", value: 1 },
    { text: "Test", value: 2 },
    { text: "Staging", value: 3 },
    { text: "Production", value: 4 }
  ];
  public gridState: State = {
    sort: [
      {
        field: "Name",
        dir: "asc",
      },
    ],
    skip: 0,
    take: 10,
  };

  constructor(
    private dataService: EnvironmentDataService,
    private envServiceDataService: EnvironmentServiceDataService,
    private dialogService: DialogService
  ) {
    super();
  }

  ngOnInit() {
    this.environmentTypeMap[1] = "Development";
    this.environmentTypeMap[2] = "Test";
    this.environmentTypeMap[3] = "Staging";
    this.environmentTypeMap[4] = "Production";
    super.ngOnInit();
  }

  protected refresh() {
    this.view = this.dataService
      .get(this.gridState, "")
      .pipe(tap(() => (this.loading = false)));
  }

  public addHandler({ sender }) {
    this.closeEditor(sender);

    this.formGroup = new FormGroup({
      Id: new FormControl(0),
      Code: this.CreateCodeControl(null),
      EnvironmentTypeId: new FormControl(1, Validators.required),
      Name: this.CreateNameControl(null),
      Description: this.CreateDescriptionControl(null)
    });
    sender.addRow(this.formGroup);
  }

  public editHandler({ sender, rowIndex, dataItem }) {
    this.closeEditor(sender);

    this.formGroup = new FormGroup({
      Id: new FormControl(dataItem.Id),
      Code: this.CreateCodeControl(dataItem.Code),
      EnvironmentTypeId: new FormControl(dataItem.EnvironmentTypeId),
      Name: this.CreateNameControl(dataItem.Name),
      Description: this.CreateDescriptionControl(dataItem.Description)
    });
    this.editedRowIndex = rowIndex;
    sender.editRow(rowIndex, this.formGroup);
  }

  public saveHandler({ sender, rowIndex, formGroup, isNew }) {
    const entity: Environment = formGroup.value;
    this.loading = true;
    this.refreshObservable(this.dataService.update(entity, isNew));
    sender.closeRow(rowIndex);
  }

  public removeHandler({ dataItem }) {
    const deleteAction = { text: "Delete", primary: true };
    const cancelAction = { text: "Cancel" };

    this.envServiceDataService.linkCountByEnvironment(+dataItem.Id)
    .subscribe(ec => 
      {
        if (+ec['@odata.count'] > 0) {
          let phrase = 'service';
          if (+ec['@odata.count'] > 1) {
            phrase = phrase + 's';
          }
          this.dialogService.open({
            title: "Cannot Delete",
            content: `This environment is associated with ${ec['@odata.count']} ${phrase}`,
            actions: [cancelAction],
          });
          return;
        }

        const dialog: DialogRef = this.dialogService.open({
          title: "Please confirm",
          content: "Deleting this environment cannot be undone.",
          actions: [cancelAction, deleteAction],
        });
    
        dialog.result.subscribe((result) => {
          if (result === deleteAction) {
            this.refreshObservable(this.dataService.delete(dataItem.Id));
          }
        });
      });
  }

  private CreateCodeControl(code){
    return new FormControl(code ?? 'Code', {updateOn: 'blur', validators: [Validators.required, Validators.minLength(3), Validators.maxLength(25), Validators.pattern('^[0-9a-zA-Z\._\-]*$')], asyncValidators: [this.UniqueCode.bind(this)]});
  }

  private CreateNameControl(name){
    return new FormControl(name ?? 'Environment Name', [Validators.required, Validators.minLength(3), Validators.maxLength(50), Validators.pattern('^[0-9a-zA-Z \.:_\-]*$')]);
  }

  private CreateDescriptionControl(description){
    return new FormControl(description ?? 'Environment Description', [Validators.maxLength(200), Validators.pattern('^[0-9a-zA-Z \.:;_\-]*$')]);
  }

  UniqueCode(ctl: AbstractControl) : Observable<ValidationErrors | null> {
    if (ctl.pristine)
      return of(null);

    let idCtl = ctl.parent.controls['Id'];

    return this.dataService.environmentCount(+idCtl.value, ctl.value)
    .pipe(
      map(
        r => 
        +r["@odata.count"] > 0 ? { unique: true } : null),
      catchError(() => of(null))
    );
  }  
}
